// ─────────────────────────────────────────────────────────────────
// Sehat AI — Full-Page Voice Overlay  (Modern Redesign)
// • Morphing liquid blob + neon equalizer bars
// • Split layout: voice left | doctors right (when available)
// ─────────────────────────────────────────────────────────────────

import React, { useEffect, useRef, useState } from 'react';
import { Volume2, Mic, Mic2, MessageCircle, CheckCircle, MapPin, Phone, Navigation, X, Send, Pill } from 'lucide-react';
import { SPECIALTY_LABELS } from '../data/specialties';

// ── Tiny doctor card for the right panel ─────────────────────────
function OverlayDoctorCard({ doc, index }) {
  const stars = Math.round(doc.rating || 0);
  const openColor = doc.open === true ? '#4ADE80' : doc.open === false ? '#F87171' : '#94A3B8';
  const openLabel = doc.open === true ? 'Open' : doc.open === false ? 'Closed' : 'Call ahead';

  return (
    <div style={{
      background: 'rgba(255,255,255,0.06)',
      border: '1px solid rgba(255,255,255,0.10)',
      borderRadius: 16,
      padding: '12px 14px',
      marginBottom: 10,
      animation: `voCardIn 0.35s ease ${index * 0.07}s both`,
      backdropFilter: 'blur(6px)',
    }}>
      {/* Name row */}
      <div style={{
        fontFamily: "'Nunito',sans-serif", fontWeight: 800,
        fontSize: 13.5, color: '#F1F5F9',
        marginBottom: 5, lineHeight: 1.3,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {doc.name}
      </div>

      {/* Rating + reviews */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
        <div style={{ display: 'flex', gap: 1 }}>
          {[1,2,3,4,5].map(s => (
            <span key={s} style={{ fontSize: 10, color: s <= stars ? '#FBBF24' : '#374151' }}>★</span>
          ))}
        </div>
        <span style={{ fontSize: 11, color: '#94A3B8', fontFamily: "'Nunito',sans-serif", fontWeight: 700 }}>
          {doc.rating} · {typeof doc.reviews === 'number' ? doc.reviews.toLocaleString() : doc.reviews} reviews
        </span>
      </div>

      {/* Distance + open status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 11, color: '#64748B', fontFamily: "'Nunito',sans-serif", fontWeight: 700, display: 'flex', alignItems: 'center', gap: 3 }}>
          <MapPin size={10} /> {doc.distance}
        </span>
        <span style={{
          fontSize: 10, fontWeight: 800, fontFamily: "'Nunito',sans-serif",
          color: openColor, background: openColor + '22', borderRadius: 6,
          padding: '2px 7px',
        }}>
          {openLabel}
        </span>
      </div>

      {/* Call + Directions buttons */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
        {doc.phone ? (
          <a href={`tel:${doc.phone}`} style={{
            flex: 1, minWidth: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
            background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.35)',
            borderRadius: 10, padding: '7px 10px', textDecoration: 'none',
            fontSize: 11.5, color: '#4ADE80', fontWeight: 800, fontFamily: "'Nunito',sans-serif",
          }}>
            <Phone size={12} /> Call Now
          </a>
        ) : (
          <span style={{
            flex: 1, minWidth: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
            background: 'rgba(100,116,139,0.12)', border: '1px solid rgba(100,116,139,0.25)',
            borderRadius: 10, padding: '7px 10px',
            fontSize: 11, color: '#475569', fontWeight: 700, fontFamily: "'Nunito',sans-serif",
          }}>
            <Phone size={12} /> Not Listed
          </span>
        )}

        <a
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(doc.name + ' ' + (doc.address || ''))}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            flex: 1, minWidth: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
            background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.35)',
            borderRadius: 10, padding: '7px 10px', textDecoration: 'none',
            fontSize: 11.5, color: '#60A5FA', fontWeight: 800, fontFamily: "'Nunito',sans-serif",
          }}
        >
          <Navigation size={12} /> Directions
        </a>
      </div>
    </div>
  );
}

// ── Equalizer bars ────────────────────────────────────────────────
const BAR_H = [10,26,16,42,24,52,34,18,46,28,54,36,12,48,20,56,30,10,44,22];

function EqBars({ color, glow, animating, loading, compact }) {
  const count = compact ? 14 : 20;
  const speed = animating ? (color === '#FF6B35' ? 0.26 : 0.46) : 1.8;
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      gap: compact ? 3 : 4,
      height: compact ? 44 : 60,
    }}>
      {BAR_H.slice(0, count).map((h, i) => (
        <div key={i} style={{
          width: compact ? 3.5 : 5,
          borderRadius: '3px 3px 1px 1px',
          background: `linear-gradient(to top, ${color}, ${color}99)`,
          boxShadow: animating ? `0 0 8px ${glow}` : 'none',
          height: animating ? h : (loading ? 7 : 3),
          opacity: animating ? 0.95 : (loading ? 0.45 : 0.18),
          animation: animating
            ? `sw ${(speed + (i % 7) * 0.055).toFixed(2)}s ease ${((i % 9) * 0.048).toFixed(3)}s infinite alternate`
            : loading
              ? `voBreathe 1.9s ease ${(i * 0.06 % 0.9).toFixed(2)}s infinite alternate`
              : 'none',
          transition: 'height 0.55s ease, opacity 0.45s',
        }} />
      ))}
    </div>
  );
}

// ── Morphing blob orb ─────────────────────────────────────────────
function MorphOrb({ color, glow, glow2, grad1, grad2, animating, loading, icon, compact }) {
  const size = compact ? 130 : 200;
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      {/* Outer ambient glow */}
      <div style={{
        position: 'absolute',
        inset: -size * 0.25,
        borderRadius: '50%',
        background: `radial-gradient(circle, ${glow2} 0%, transparent 68%)`,
        animation: animating ? 'voHalo 1.6s ease infinite' : 'none',
        pointerEvents: 'none',
        transition: 'background 0.7s ease',
      }} />

      {/* Morphing liquid blob */}
      <div style={{
        position: 'absolute', inset: 0,
        background: `linear-gradient(135deg, ${grad1} 0%, ${grad2} 100%)`,
        boxShadow: `0 0 ${compact ? 30 : 50}px ${glow}, 0 0 ${compact ? 60 : 100}px ${glow2}, inset 0 1px 0 rgba(255,255,255,0.25)`,
        animation: animating
          ? 'morphBlob 3.8s ease infinite'
          : loading
            ? 'morphBlobSlow 5s ease infinite'
            : 'morphBlobIdle 7s ease infinite',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'background 0.7s ease, box-shadow 0.7s ease',
      }}>
        {/* Shimmer layer */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(135deg, rgba(255,255,255,0.18) 0%, transparent 50%)',
          borderRadius: 'inherit',
          pointerEvents: 'none',
        }} />
        <span style={{
          filter: 'drop-shadow(0 2px 16px rgba(0,0,0,0.6))',
          position: 'relative', zIndex: 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {React.createElement(icon, { size: compact ? 38 : 56, color: 'rgba(255,255,255,0.92)', strokeWidth: 1.5 })}
        </span>
      </div>

      {/* Spinner ring for thinking state */}
      {loading && (
        <div style={{
          position: 'absolute', inset: -8, borderRadius: '50%',
          border: '2px solid transparent',
          borderTopColor: color,
          borderRightColor: color + '44',
          animation: 'voSpin 1s linear infinite',
        }} />
      )}
    </div>
  );
}

// ── Main Overlay ──────────────────────────────────────────────────
// ── Tablet chip ───────────────────────────────────────────────────
function TabletChip({ med, index }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      background: `${med.color}14`,
      border: `1px solid ${med.color}40`,
      borderRadius: 12, padding: '8px 13px',
      animation: `voCardIn 0.3s ease ${index * 0.06}s both`,
      minWidth: 0,
    }}>
      <span style={{ fontSize: 18, flexShrink: 0 }}>{med.emoji}</span>
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontFamily: "'Nunito',sans-serif", fontWeight: 800,
          fontSize: 13, color: '#F1F5F9',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {med.name}
        </div>
        <div style={{
          fontFamily: "'Nunito',sans-serif", fontWeight: 600,
          fontSize: 10.5, color: med.color, marginTop: 1,
        }}>
          {med.use}
        </div>
      </div>
    </div>
  );
}

export default function VoiceOverlay({ active, listening, isSpeaking, loading, interim, onEnd, doctors = [], specialty, tablets = [], isSendPending = false, onSendNow }) {
  const [mounted, setMounted] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 640);
  const fadeTimer = useRef(null);

  useEffect(() => {
    if (active) {
      clearTimeout(fadeTimer.current);
      setMounted(true);
    } else {
      fadeTimer.current = setTimeout(() => setMounted(false), 380);
    }
    return () => clearTimeout(fadeTimer.current);
  }, [active]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (!mounted) return null;

  const hasDoctors = doctors.length > 0;
  const hasTablets = tablets.length > 0;
  // On mobile with doctors, use vertical stacked layout
  const mobileStack = isMobile && hasDoctors;

  // State theming
  let color, glow, glow2, grad1, grad2, label, subLabel, icon, animating;

  if (isSpeaking) {
    color    = '#FF6B35'; glow = 'rgba(255,107,53,0.6)'; glow2 = 'rgba(255,107,53,0.2)';
    grad1    = '#FF6B35'; grad2 = '#FFB347';
    label    = 'Sehat AI bol rahi hai'; subLabel = 'Suno dhyan se…';
    icon     = Volume2; animating = true;
  } else if (isSendPending) {
    color    = '#34D399'; glow = 'rgba(52,211,153,0.55)'; glow2 = 'rgba(52,211,153,0.15)';
    grad1    = '#10B981'; grad2 = '#34D399';
    label    = 'Bhejna wala hoon…'; subLabel = 'Ya abhi bolo — main sun rahi hoon';
    icon     = CheckCircle; animating = false;
  } else if (loading) {
    color    = '#A78BFA'; glow = 'rgba(167,139,250,0.55)'; glow2 = 'rgba(167,139,250,0.18)';
    grad1    = '#8B5CF6'; grad2 = '#C084FC';
    label    = 'Samajh rahi hoon'; subLabel = 'Ek pal…';
    icon     = MessageCircle; animating = false;
  } else if (listening) {
    color    = '#22D3EE'; glow = 'rgba(34,211,238,0.6)'; glow2 = 'rgba(34,211,238,0.18)';
    grad1    = '#06B6D4'; grad2 = '#67E8F9';
    label    = 'Sun rahi hoon'; subLabel = 'Boliye ji…';
    icon     = Mic; animating = true;
  } else {
    color    = '#94A3B8'; glow = 'rgba(148,163,184,0.4)'; glow2 = 'rgba(148,163,184,0.1)';
    grad1    = '#64748B'; grad2 = '#94A3B8';
    label    = 'Taiyaar hoon'; subLabel = 'Ek moment…';
    icon     = Mic2; animating = false;
  }

  return (
    <>
      <div style={{
        position: 'fixed', inset: 0, zIndex: 999,
        background: '#070714',
        display: 'flex',
        flexDirection: mobileStack ? 'column' : 'row',
        animation: active ? 'voFadeIn 0.4s cubic-bezier(0.22,1,0.36,1)' : 'voFadeOut 0.38s ease forwards',
        overflow: 'hidden',
      }}>

        {/* ── Background mesh gradients ──────────────────────── */}
        <div style={{
          position: 'absolute', width: 600, height: 600, borderRadius: '50%',
          top: '-20%', left: hasDoctors ? '-15%' : '-5%',
          background: `radial-gradient(circle, ${glow2} 0%, transparent 65%)`,
          transition: 'background 0.8s ease', pointerEvents: 'none',
        }} />
        <div style={{
          position: 'absolute', width: 400, height: 400, borderRadius: '50%',
          bottom: '-15%', right: hasDoctors ? '42%' : '-5%',
          background: `radial-gradient(circle, ${glow2} 0%, transparent 65%)`,
          transition: 'all 0.8s ease', pointerEvents: 'none',
        }} />

        {/* ════════════════════════════════════════════════════════
            TOP / LEFT PANEL — Voice Animation
        ════════════════════════════════════════════════════════ */}
        <div style={{
          flex: mobileStack ? '0 0 auto' : (hasDoctors ? '0 0 44%' : '1'),
          boxSizing: 'border-box',
          minWidth: 0,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center',
          justifyContent: mobileStack ? 'flex-start' : 'center',
          padding: mobileStack ? '20px 20px 14px' : (hasDoctors ? '28px 20px' : '28px'),
          position: 'relative',
        }}>
          {/* Brand tag — hide on mobile stacked to save space */}
          {!mobileStack && (
            <div style={{
              fontFamily: "'Nunito',sans-serif", fontWeight: 900,
              fontSize: 11, letterSpacing: 3.5, textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.22)', marginBottom: hasDoctors ? 28 : 40,
            }}>
              SEHAT AI · VOICE
            </div>
          )}

          {/* Morphing orb — row on mobile: orb + status side by side */}
          {mobileStack ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, width: '100%' }}>
              <MorphOrb
                color={color} glow={glow} glow2={glow2}
                grad1={grad1} grad2={grad2}
                animating={animating} loading={loading}
                icon={icon} compact={true}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontFamily: "'Nunito',sans-serif", fontWeight: 800,
                  fontSize: 17, color,
                  textShadow: `0 0 14px ${glow}`,
                  marginBottom: 3, transition: 'color 0.5s',
                }}>
                  {label}
                </div>
                <div style={{
                  fontFamily: "'Nunito',sans-serif", fontWeight: 600,
                  fontSize: 12, color: 'rgba(255,255,255,0.35)',
                }}>
                  {subLabel}
                </div>
                <EqBars color={color} glow={glow} animating={animating} loading={loading} compact={true} />
              </div>
              {/* End button inline on mobile */}
              <button
                onClick={onEnd}
                style={{
                  flexShrink: 0,
                  background: 'rgba(239,68,68,0.18)',
                  border: '1.5px solid rgba(239,68,68,0.4)',
                  borderRadius: 50, padding: '8px 16px',
                  color: '#F87171', fontSize: 12, fontWeight: 800,
                  fontFamily: "'Nunito',sans-serif", cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                <X size={14} /> End
              </button>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: hasDoctors ? 24 : 36 }}>
                <MorphOrb
                  color={color} glow={glow} glow2={glow2}
                  grad1={grad1} grad2={grad2}
                  animating={animating} loading={loading}
                  icon={icon} compact={hasDoctors}
                />
              </div>

              {/* Equalizer bars */}
              <div style={{ marginBottom: hasDoctors ? 18 : 28 }}>
                <EqBars color={color} glow={glow} animating={animating} loading={loading} compact={hasDoctors} />
              </div>

              {/* Status label */}
              <div style={{
                fontFamily: "'Nunito',sans-serif", fontWeight: 800,
                fontSize: hasDoctors ? 16 : 22,
                color, textShadow: `0 0 20px ${glow}`,
                marginBottom: 4, transition: 'color 0.5s, text-shadow 0.5s',
                textAlign: 'center',
              }}>
                {label}
              </div>
              <div style={{
                fontFamily: "'Nunito',sans-serif", fontWeight: 600,
                fontSize: 12, color: 'rgba(255,255,255,0.30)',
                marginBottom: hasDoctors ? 14 : 20, letterSpacing: 0.4,
              }}>
                {subLabel}
              </div>
            </>
          )}

          {/* Interim transcript */}
          {interim && (
            <div style={{
              maxWidth: mobileStack ? '100%' : (hasDoctors ? 260 : 320), width: '100%',
              marginTop: mobileStack ? 10 : 0,
              marginBottom: hasDoctors ? 16 : 24,
              background: 'rgba(255,255,255,0.07)',
              backdropFilter: 'blur(16px)',
              border: `1px solid ${color}44`,
              borderRadius: 16, padding: '10px 16px',
              fontFamily: "'Nunito',sans-serif",
              fontSize: hasDoctors ? 13 : 15,
              fontWeight: 600, fontStyle: 'italic',
              color: 'rgba(255,255,255,0.85)',
              textAlign: 'center', lineHeight: 1.6,
              animation: 'voSlideUp 0.22s ease',
            }}>
              "{interim}"
            </div>
          )}

          {/* Send-pending: countdown bar + "Send Now" tap button */}
          {isSendPending && (
            <div style={{ width: '100%', maxWidth: mobileStack ? '100%' : 340, marginBottom: 16, marginTop: mobileStack ? 8 : 0 }}>
              <div style={{
                height: 3, borderRadius: 3, background: 'rgba(255,255,255,0.10)',
                overflow: 'hidden', marginBottom: 10,
              }}>
                <div style={{
                  height: '100%', borderRadius: 3,
                  background: `linear-gradient(to right, ${color}, ${color}88)`,
                  animation: 'voDrain 2.5s linear forwards',
                }} />
              </div>
              <button onClick={onSendNow} style={{
                width: '100%', padding: '10px 0',
                background: `${color}22`, border: `1.5px solid ${color}55`,
                borderRadius: 14, color,
                fontFamily: "'Nunito',sans-serif", fontWeight: 800,
                fontSize: 13, cursor: 'pointer', letterSpacing: 0.4,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
              }}>
                <Send size={14} /> Abhi Bhejo
              </button>
            </div>
          )}

          {/* Tablet chips — left/top panel when no doctors */}
          {hasTablets && !hasDoctors && (
            <div style={{ width: '100%', maxWidth: mobileStack ? '100%' : 340, marginBottom: 18 }}>
              <div style={{
                fontFamily: "'Nunito',sans-serif", fontWeight: 900,
                fontSize: 10, letterSpacing: 2.5, textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.30)', marginBottom: 10, textAlign: 'center',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
              }}>
                <Pill size={11} /> Suggested Tablets
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: tablets.length === 1 ? '1fr' : 'repeat(2, 1fr)',
                gap: 8,
              }}>
                {tablets.map((med, i) => <TabletChip key={i} med={med} index={i} />)}
              </div>
            </div>
          )}

          {/* End button — only shown in left panel on desktop; mobile has it inline above */}
          {!mobileStack && (
            <button
              onClick={onEnd}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: 'rgba(255,255,255,0.07)',
                border: '1.5px solid rgba(255,255,255,0.15)',
                borderRadius: 50, padding: hasDoctors ? '10px 24px' : '13px 36px',
                color: 'rgba(255,255,255,0.75)',
                fontSize: 13, fontWeight: 800,
                fontFamily: "'Nunito',sans-serif",
                cursor: 'pointer', letterSpacing: 0.4,
                transition: 'all 0.22s ease',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'rgba(239,68,68,0.25)';
                e.currentTarget.style.borderColor = 'rgba(239,68,68,0.5)';
                e.currentTarget.style.color = '#fff';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.07)';
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)';
                e.currentTarget.style.color = 'rgba(255,255,255,0.75)';
              }}
            >
              <X size={14} /> End Conversation
            </button>
          )}
        </div>

        {/* ════════════════════════════════════════════════════════
            BOTTOM / RIGHT PANEL — Doctor List
        ════════════════════════════════════════════════════════ */}
        {hasDoctors && (
          <div style={{
            flex: mobileStack ? '1' : '0 0 56%',
            boxSizing: 'border-box',
            minWidth: 0,
            minHeight: 0,
            borderLeft: mobileStack ? 'none' : '1px solid rgba(255,255,255,0.07)',
            borderTop: mobileStack ? '1px solid rgba(255,255,255,0.09)' : 'none',
            display: 'flex', flexDirection: 'column',
            padding: mobileStack ? '14px 16px 16px' : '28px 20px 20px',
            background: 'rgba(255,255,255,0.025)',
            backdropFilter: 'blur(4px)',
            overflow: 'hidden',
          }}>
            {/* Panel header */}
            <div style={{ marginBottom: mobileStack ? 10 : 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
              <div>
                {!mobileStack && (
                  <div style={{
                    fontFamily: "'Nunito',sans-serif", fontWeight: 900,
                    fontSize: 11, letterSpacing: 3, textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.22)', marginBottom: 6,
                  }}>
                    NEARBY DOCTORS
                  </div>
                )}
                <div style={{
                  fontFamily: "'Nunito',sans-serif", fontWeight: 800,
                  fontSize: mobileStack ? 14 : 16, color: '#F1F5F9', lineHeight: 1.3,
                }}>
                  {doctors.length} {SPECIALTY_LABELS[specialty] || specialty || 'Doctor'}{doctors.length !== 1 ? 's' : ''} Found
                </div>
                <div style={{
                  fontFamily: "'Nunito',sans-serif", fontWeight: 600,
                  fontSize: 11, color: 'rgba(255,255,255,0.32)', marginTop: 2,
                }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <MapPin size={11} /> {doctors[0]?.isReal ? 'Aapke paas ke doctors' : 'Najdeeki doctors'}
                  </span>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', marginBottom: mobileStack ? 10 : 14 }} />

            {/* Scrollable doctor cards — flex:1 so it fills remaining space above the sticky footer */}
            <div style={{ flex: 1, overflowY: 'auto', paddingRight: 4, minHeight: 0 }}>
              {doctors.map((doc, i) => (
                <OverlayDoctorCard key={i} doc={doc} index={i} />
              ))}
              <div style={{
                textAlign: 'center', marginTop: 8, padding: '6px 12px',
                fontFamily: "'Nunito',sans-serif", fontSize: 10.5,
                color: 'rgba(255,255,255,0.22)', lineHeight: 1.5,
              }}>
                ⚕️ Sehat AI provides guidance only. Always consult a registered medical professional.
              </div>
            </div>

            {/* ── Suggested Tablets — pinned at bottom, never scrolls away ── */}
            {hasTablets && (
              <div style={{
                flexShrink: 0,
                borderTop: '1px solid rgba(255,255,255,0.09)',
                paddingTop: 12,
                marginTop: 10,
                background: 'rgba(0,0,0,0.25)',
                backdropFilter: 'blur(8px)',
                borderRadius: '0 0 0 0',
              }}>
                <div style={{
                  fontFamily: "'Nunito',sans-serif", fontWeight: 900,
                  fontSize: 10, letterSpacing: 2.5, textTransform: 'uppercase',
                  color: 'rgba(255,255,255,0.35)', marginBottom: 9,
                  display: 'flex', alignItems: 'center', gap: 5,
                }}>
                  <Pill size={11} /> Suggested Tablets
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: 7,
                }}>
                  {tablets.map((med, i) => <TabletChip key={i} med={med} index={i} />)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Animation keyframes ─────────────────────────────────── */}
      <style>{`
        @keyframes voFadeIn {
          from { opacity: 0; transform: scale(1.03); }
          to   { opacity: 1; transform: scale(1);    }
        }
        @keyframes voFadeOut {
          from { opacity: 1; transform: scale(1);    }
          to   { opacity: 0; transform: scale(0.97); }
        }
        @keyframes morphBlob {
          0%   { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; transform: scale(1); }
          20%  { border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%; transform: scale(1.04); }
          40%  { border-radius: 50% 60% 30% 40% / 40% 50% 60% 70%; transform: scale(0.97); }
          60%  { border-radius: 40% 70% 60% 30% / 60% 40% 40% 60%; transform: scale(1.05); }
          80%  { border-radius: 70% 30% 40% 60% / 30% 60% 50% 40%; transform: scale(0.98); }
          100% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; transform: scale(1); }
        }
        @keyframes morphBlobSlow {
          0%,100% { border-radius: 50% 50% 40% 60% / 50% 40% 60% 50%; transform: scale(1); }
          50%     { border-radius: 40% 60% 60% 40% / 60% 40% 50% 50%; transform: scale(1.03); }
        }
        @keyframes morphBlobIdle {
          0%,100% { border-radius: 50%; transform: scale(1); }
          50%     { border-radius: 45% 55% 55% 45% / 50% 48% 52% 50%; transform: scale(1.02); }
        }
        @keyframes voHalo {
          0%,100% { transform: scale(0.9);  opacity: 0.6; }
          50%     { transform: scale(1.15); opacity: 1;   }
        }
        @keyframes voBreathe {
          from { transform: scaleY(0.4); opacity: 0.28; }
          to   { transform: scaleY(1.5); opacity: 0.65; }
        }
        @keyframes voSpin {
          from { transform: rotate(0deg);   }
          to   { transform: rotate(360deg); }
        }
        @keyframes voSlideUp {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
        @keyframes voDrain {
          from { width: 100%; }
          to   { width: 0%;   }
        }
        @keyframes voCardIn {
          from { opacity: 0; transform: translateX(20px); }
          to   { opacity: 1; transform: translateX(0);    }
        }
        /* Scrollbar for doctor panel */
        .vo-scroll::-webkit-scrollbar { width: 3px; }
        .vo-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 3px; }
      `}</style>
    </>
  );
}
